import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import usePageTitle from "../../hooks/usePageTitle";
import { ReactFlowProvider } from "@xyflow/react";
import { APIs } from "../../services/Apis";
import { DatabaseDetailsPanel } from "../../components/panels/DatabaseDetailsPanel";
import { MultiStepLoaderSegments } from "../../components/loaders/MultiStepLoaderSegments";
import { RegisterModal } from "../../components/dialogs/RegisterModal";
import { convertStakeholdersToFlowDiagram } from "../../services/FlowConstants";
import { BusinessDetailsStep, GoalStep, SegmentStep } from ".";
import { SENTENCE_PARTS } from "./GoalStep";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { Button } from "../../components/Button";
import "@xyflow/react/dist/style.css";
import { toast } from "react-toastify";

const ONBOARDING_STEP = Object.freeze({
  BUSINESS_DETAILS: 0,
  GOAL: 1,
  FLOW_DIAGRAM: 2,
  REVIEW: 3,
});

export default function GetStartedPage() {
  usePageTitle("Get Started");
  const [databasePanelOpen, setDatabasePanelOpen] = useState(false);
  const maxSteps = 3;
  const [currentStep, setCurrentStep] = useState(0);
  const [nextButtonCopy, setNextButtonCopy] = useState("Confirm & Continue");
  const [searchParams] = useSearchParams();
  const [url, setUrl] = useState("");
  const navigate = useNavigate();
  const [nextButtonLoading, setNextButtonLoading] = useState(false);
  const [stepData, setStepData] = useState({});
  const [segments, setSegments] = useState([]);
  const [segmentEdges, setSegmentEdges] = useState([]);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [businessDetailsLoading, setBusinessDetailsLoading] = useState(false);
  // A Business DNA row that is open for editing holds uncommitted changes, so
  // continuing past it would carry the old values forward.
  const [businessDetailsEditing, setBusinessDetailsEditing] = useState(false);
  const [goal, setGoal] = useState({});
  const [examples, setExamples] = useState({});
  const [examplesLoading, setExamplesLoading] = useState(false);
  const [segmentsDataReady, setSegmentsDataReady] = useState(false);
  const pendingSegmentsRef = useRef(null);

  // A step is mounted the first time it is reached and then kept alive, so
  // going back never remounts it — the form keeps what was typed, and the
  // step's loader doesn't replay.
  const [visitedSteps, setVisitedSteps] = useState(
    () => new Set([ONBOARDING_STEP.BUSINESS_DETAILS]),
  );

  // Both getBusinessExamples and getSegments are built from the business
  // details alone, so a step only refetches when those details actually
  // changed since its last successful call.
  const examplesSignatureRef = useRef(null);
  const segmentsSignatureRef = useRef(null);

  // Bumped whenever a new set of goal options arrives, so the kept-alive goal
  // step remounts against them instead of holding answers to the old ones.
  const [examplesVersion, setExamplesVersion] = useState(0);

  const businessSignature = useCallback(
    () =>
      JSON.stringify({
        businessName: stepData.businessName,
        websiteUrl: stepData.websiteUrl,
        businessDescription: stepData.businessDescription,
        stakeholders: stepData.stakeholders,
        associated_urls: stepData.associated_urls,
      }),
    [stepData],
  );

  useEffect(() => {
    setVisitedSteps((prev) => {
      if (prev.has(currentStep)) return prev;
      const next = new Set(prev);
      next.add(currentStep);
      return next;
    });
  }, [currentStep]);

  useEffect(() => {
    const urlParam = searchParams.get("url");
    if (urlParam) {
      setUrl(decodeURIComponent(urlParam));
    } else {
      toast.error("Please enter a valid URL");
      navigate("/");
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (currentStep === maxSteps - 1) {
      setNextButtonCopy("Confirm & Continue");
    } else if (currentStep === maxSteps) {
      setNextButtonCopy("Confirm & Continue");
    } else {
      setNextButtonCopy("Confirm & Continue");
    }
  }, [currentStep, maxSteps]);

  function handleNext() {
    if (!isStepValid()) {
      toast.error("Please fill in all fields");
      return;
    }

    if (currentStep === ONBOARDING_STEP.BUSINESS_DETAILS) {
      const signature = businessSignature();
      // Nothing changed while they were back on this step, so keep the options
      // they have already answered against instead of refetching them.
      if (
        examplesSignatureRef.current === signature &&
        Object.keys(examples).length > 0
      ) {
        proceedToNextStep();
        return;
      }
      getBusinessExamples(signature);
      return;
    }

    if (currentStep === ONBOARDING_STEP.GOAL) {
      const signature = businessSignature();
      // Same business details as the last run, so the segments we already have
      // are the segments the API would return — skip the loader entirely.
      if (segmentsSignatureRef.current === signature && segments.length > 0) {
        proceedToNextStep();
        return;
      }
      setNextButtonLoading(true);
      getSegments(true, true, signature);
      return;
    }

    if (currentStep === ONBOARDING_STEP.FLOW_DIAGRAM) {
      setRegisterModalOpen(true);
      return;
    }

    if (currentStep === ONBOARDING_STEP.REVIEW) {
      return;
    }

    if (currentStep === maxSteps) {
      return;
    }
  }

  const getSegments = async (
    showLoader = true,
    shouldProceedToNextStep = true,
    signature = null,
  ) => {
    if (showLoader) {
      setNextButtonLoading(true);
      setSegmentsDataReady(false);
    }

    try {
      const responseData = await APIs.getSegments(
        stepData.businessName,
        stepData.websiteUrl,
        stepData.businessDescription,
        stepData.stakeholders,
        stepData.associated_urls,
      );

      if (responseData && responseData.success) {
        const { nodes, edges } = convertStakeholdersToFlowDiagram(
          responseData.segments,
        );
        let sortedSegments = responseData.segments.map((segment) => ({
          ...segment,
          triggers: [...segment.triggers]
            .sort((a, b) => Number(a.sortIndex) - Number(b.sortIndex))
            .map((trigger) => ({
              ...trigger,
              actions: [...trigger.actions].sort(
                (a, b) => Number(a.sortIndex) - Number(b.sortIndex),
              ),
            })),
        }));

        if (signature) {
          segmentsSignatureRef.current = signature;
        }

        if (showLoader) {
          pendingSegmentsRef.current = { sortedSegments, edges, shouldProceedToNextStep };
          setSegmentsDataReady(true);
        } else {
          setSegments(sortedSegments);
          setSegmentEdges(edges);
          if (shouldProceedToNextStep) {
            proceedToNextStep();
          }
        }
      }
    } catch (err) {
      console.error("Failed to submit business details:", err);
      setNextButtonLoading(false);
    }
  };

  // Options for the goal sentence, fetched from the same business details the
  // segments are built from.
  const getBusinessExamples = async (signature = null) => {
    setExamplesLoading(true);

    try {
      const responseData = await APIs.getBusinessExamples(
        stepData.businessName,
        stepData.websiteUrl,
        stepData.businessDescription,
        stepData.stakeholders,
        stepData.associated_urls,
      );

      if (responseData && responseData.success) {
        const { success, ...options } = responseData;
        setExamples(options);
        // Answers were picked from the previous set of options, so they no
        // longer mean anything against these ones.
        if (
          examplesSignatureRef.current !== null &&
          examplesSignatureRef.current !== signature
        ) {
          setGoal({});
        }
        if (signature) {
          examplesSignatureRef.current = signature;
        }
        setExamplesVersion((prev) => prev + 1);
        proceedToNextStep();
      } else {
        toast.error("Couldn't load your goal options. Please try again.");
      }
    } catch (err) {
      console.error("Failed to fetch business examples:", err);
      toast.error("Couldn't load your goal options. Please try again.");
    } finally {
      setExamplesLoading(false);
    }
  };

  const handleRefreshSegments = () => {
    getSegments(false, false);
  };

  const handleLoaderComplete = useCallback(() => {
    const pending = pendingSegmentsRef.current;
    if (!pending) return;

    setNextButtonLoading(false);
    setSegments(pending.sortedSegments);
    setSegmentEdges(pending.edges);
    if (pending.shouldProceedToNextStep) {
      setCurrentStep((prev) => prev + 1);
    }
    pendingSegmentsRef.current = null;
    setSegmentsDataReady(false);
  }, []);

  const allReady = useMemo(() => {
    if (!segments.length) return true;
    return segments.every((segment) => {
      const triggers = segment.triggers || [];
      return triggers.every((trigger) => {
        const actions = trigger.actions || [];
        return actions.every((action) => action.ready !== false);
      });
    });
  }, [segments]);

  useEffect(() => {
    if (currentStep !== ONBOARDING_STEP.FLOW_DIAGRAM) return;
    if (allReady) return;

    const intervalId = setInterval(() => {
      getSegments(false, false);
    }, 15000);

    return () => clearInterval(intervalId);
  }, [currentStep, allReady]);

  function proceedToNextStep() {
    setCurrentStep(currentStep + 1);
  }

  function handleBack() {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }

  const handleBusinessDataChange = (data) => {
    setStepData(data);
  };

  const isStepValid = () => {
    switch (currentStep) {
      case ONBOARDING_STEP.BUSINESS_DETAILS:
        const hasValidStakeholders =
          stepData.stakeholders &&
          stepData.stakeholders.length > 0 &&
          stepData.stakeholders.every(
            (stakeholder) => stakeholder.name && stakeholder.description,
          );
        return (
          stepData.businessName &&
          stepData.businessDescription &&
          hasValidStakeholders
        );
      case ONBOARDING_STEP.GOAL:
        // Every part the API returned options for has to be answered.
        return SENTENCE_PARTS.filter((part) => examples[part.id]?.length).every(
          (part) => goal[part.id],
        );
      case ONBOARDING_STEP.FLOW_DIAGRAM:
        return true;
      case ONBOARDING_STEP.REVIEW:
        return true;
      default:
        return false;
    }
  };

  // Visited steps stay in the tree and are hidden rather than unmounted, so
  // stepping back and forward preserves whatever was typed or picked.
  const stepPane = (step, node) =>
    visitedSteps.has(step) ? (
      <div style={{ display: currentStep === step ? "contents" : "none" }}>
        {node}
      </div>
    ) : null;

  return (
    <div className="min-h-screen overflow-x-auto">
      <ReactFlowProvider>
        {stepPane(
          ONBOARDING_STEP.BUSINESS_DETAILS,
          <BusinessDetailsStep
            url={url}
            onDataChange={handleBusinessDataChange}
            onLoadingChange={setBusinessDetailsLoading}
            onEditingChange={setBusinessDetailsEditing}
          />,
        )}
        {stepPane(
          ONBOARDING_STEP.GOAL,
          <GoalStep
            key={examplesVersion}
            examples={examples}
            value={goal}
            onChange={setGoal}
            active={currentStep === ONBOARDING_STEP.GOAL}
          />,
        )}
        {stepPane(
          ONBOARDING_STEP.FLOW_DIAGRAM,
          <SegmentStep
            segments={segments}
            segmentEdges={segmentEdges}
            onRefresh={handleRefreshSegments}
            businessUrl={url}
            onOpenSignup={() => setRegisterModalOpen(true)}
          />,
        )}
      </ReactFlowProvider>

      {/* Rendered over the steps rather than in place of them, so everything
          underneath stays mounted while the segments are generated. */}
      <MultiStepLoaderSegments
        loading={nextButtonLoading}
        dataReady={segmentsDataReady}
        onComplete={handleLoaderComplete}
        businessName={stepData.businessName}
        businessUrl={stepData.websiteUrl}
        businessDescription={stepData.businessDescription}
        stakeholders={stepData.stakeholders}
      />

      {!businessDetailsLoading && !nextButtonLoading && (
        <div
          className="bg-white rounded-3xl p-4 border border-slate-200 flex items-center justify-center gap-2"
          style={{
            position: "fixed",
            bottom: "32px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: "20",
          }}
        >
          {currentStep > 0 && (
            <Button
              variant="outline"
              color="slate"
              size="lg"
              onClick={handleBack}
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
          <Button
            variant="solid"
            color="primary"
            size="lg"
            onClick={handleNext}
            loading={examplesLoading}
            disabled={
              (currentStep === ONBOARDING_STEP.GOAL && !isStepValid()) ||
              (currentStep === ONBOARDING_STEP.BUSINESS_DETAILS &&
                businessDetailsEditing)
            }
            className="disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
          >
            {nextButtonCopy}
            {currentStep < maxSteps && (
              <ChevronRightIcon className="h-4 w-4 ml-1" />
            )}
          </Button>
        </div>
      )}
      <DatabaseDetailsPanel
        isOpen={databasePanelOpen}
        onClose={() => setDatabasePanelOpen(false)}
      />
      <RegisterModal
        isOpen={registerModalOpen}
        onClose={() => setRegisterModalOpen(false)}
        businessUrl={url}
      />
    </div>
  );
}
